import React, { useMemo } from 'react';
import { CalendarClock, LockKeyhole, ReceiptText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth, type User } from '../../context/AuthContext';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface ProgramAccessBannerState {
  phase: 'active' | 'grace' | 'expired';
  date: string;
  daysRemaining: number;
}

const parseRemainingDays = (value: string | null | undefined, now: number) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.ceil((timestamp - now) / DAY_MS));
};

export const resolveProgramAccessBannerState = (
  user: User | null,
  now = Date.now(),
): ProgramAccessBannerState | null => {
  const courtesy = user?.programCourtesy;
  if (!courtesy) return null;

  const date = courtesy.phase === 'active' ? courtesy.accessEndsAt : courtesy.graceEndsAt;
  const daysRemaining = parseRemainingDays(date, now);
  if (!date || daysRemaining === null) return null;
  if (courtesy.phase === 'active' && daysRemaining > 90) return null;

  return { phase: courtesy.phase, date, daysRemaining };
};

const formatDate = (value: string, locale: string) => new Intl.DateTimeFormat(locale, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}).format(new Date(value));

const ProgramAccessBanner: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const state = useMemo(() => resolveProgramAccessBannerState(user), [user]);

  if (!state) return null;

  const locale = String(i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('fr')
    ? 'fr-CA'
    : 'en-CA';
  const date = formatDate(state.date, locale);
  const isActive = state.phase === 'active';
  const isGrace = state.phase === 'grace';
  const Icon = isActive ? CalendarClock : isGrace ? ReceiptText : LockKeyhole;

  return (
    <aside
      aria-live="polite"
      aria-label={t('programAccessBannerLabel')}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderBottom: '1px solid var(--border-primary)',
        background: 'var(--card-bg)',
        color: 'var(--text-primary)',
      }}
    >
      <div
        style={{
          maxWidth: '1440px',
          minWidth: 0,
          margin: '0 auto',
          padding: '10px clamp(16px, 3vw, 32px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px 16px',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <Icon size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px', color: isGrace ? 'var(--warning-text)' : 'var(--accent-text)' }} />
          <div style={{ minWidth: 0 }}>
            <strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.35 }}>
              {t(`programAccessBanner.${state.phase}.title`)}
            </strong>
            <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.45 }}>
              {t(`programAccessBanner.${state.phase}.description`, {
                count: state.daysRemaining,
                date,
              })}
            </span>
          </div>
        </div>
        {!isActive && (
          <Link
            to="/pricing"
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '44px',
              padding: '0 12px',
              borderRadius: '8px',
              background: 'var(--accent-solid)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            {t('programAccessBanner.reviewPlans')}
          </Link>
        )}
      </div>
    </aside>
  );
};

export default ProgramAccessBanner;
